import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { BrainIcon, RepeatIcon, ActivityIcon, ArrowLeftRightIcon, RefreshCwIcon, TargetIcon, SparklesIcon, EyeIcon, FileTextIcon, PenToolIcon, CalendarIcon, GiftIcon } from 'lucide-react';
export function SalesLetterContentMap() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const laws = [{
    icon: BrainIcon,
    name: 'Mentalismo',
    subtitle: 'Todo es mente',
    description: 'Reprogramación del diálogo interno'
  }, {
    icon: RepeatIcon,
    name: 'Correspondencia',
    subtitle: 'Como es adentro, es afuera',
    description: 'Patrones en relaciones y límites'
  }, {
    icon: ActivityIcon,
    name: 'Vibración',
    subtitle: 'Todo vibra',
    description: 'Gestión de estado emocional y energía cotidiana'
  }, {
    icon: ArrowLeftRightIcon,
    name: 'Polaridad',
    subtitle: 'Dos polos',
    description: 'Crisis personal y transformación de enfoque'
  }, {
    icon: RefreshCwIcon,
    name: 'Ritmo',
    subtitle: 'Ciclos',
    description: 'Dinero, abundancia-escasez, estabilidad'
  }, {
    icon: TargetIcon,
    name: 'Causa y Efecto',
    subtitle: '',
    description: 'Hábitos financieros y decisiones conscientes'
  }, {
    icon: SparklesIcon,
    name: 'Generación',
    subtitle: '',
    description: 'Crear proyectos: intuición + estructura + acción'
  }];
  const bonuses = [{
    icon: FileTextIcon,
    title: 'BONUS 1',
    name: 'Afirmaciones por ley',
    description: 'No para repetir por repetir, sino para reprogramar con intención + acción diaria.'
  }, {
    icon: PenToolIcon,
    title: 'BONUS 2',
    name: '21 preguntas de journaling',
    description: 'Un sistema simple para romper patrones, ver tus causas invisibles y recuperar dirección.'
  }, {
    icon: CalendarIcon,
    title: 'BONUS 3',
    name: 'Plan práctico de 30 días',
    description: 'Rutina mínima + revisión semanal + estructura por semanas para sostener cambios. (checklist imprimible)'
  }];
  return <section ref={ref} className="w-full bg-gradient-to-b from-[#0a0e27] via-[#1a1a4e] to-[#2d1b69] py-20 px-4 md:py-28" data-id="element-131">
      <div className="max-w-6xl mx-auto" data-id="element-132">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-133">
          <h2 className="font-playfair text-2xl md:text-3xl lg:text-4xl text-[#d4a853] mb-4" data-id="element-134">
            Lo que hay dentro
          </h2>
          <p className="font-dm-sans text-lg text-[#f5f0e8]/70" data-id="element-135">
            (mapa del contenido)
          </p>
        </motion.div>

        <motion.p initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {}} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="font-dm-sans text-lg text-[#f5f0e8]/80 text-center mb-12" data-id="element-136">
          Este eBook te guía por un recorrido progresivo:
        </motion.p>

        {/* 7 Laws Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-12" data-id="element-137">
          {laws.map((law, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: 0.3 + index * 0.1
        }} className="relative bg-gradient-to-br from-[#1a1a4e]/80 to-[#0a0e27]/80 border border-[#d4a853]/20 rounded-xl p-5 group hover:border-[#d4a853]/50 transition-colors" data-id="element-138">
              <div className="flex items-center gap-3 mb-3" data-id="element-139">
                <div className="w-10 h-10 rounded-full bg-[#d4a853]/20 flex items-center justify-center" data-id="element-140">
                  <law.icon className="w-5 h-5 text-[#d4a853]" data-id="element-141" />
                </div>
                <div data-id="element-142">
                  <h3 className="font-playfair text-lg text-[#f0c75e]" data-id="element-143">
                    {law.name}
                  </h3>
                  {law.subtitle && <p className="font-dm-sans text-xs text-[#f5f0e8]/50" data-id="element-144">
                      ({law.subtitle})
                    </p>}
                </div>
              </div>
              <p className="font-dm-sans text-sm text-[#f5f0e8]/80" data-id="element-145">
                → {law.description}
              </p>
            </motion.div>)}

          {/* Special Chapter Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: 1
        }} className="relative bg-gradient-to-br from-[#d4a853]/20 to-[#d4a853]/10 border-2 border-[#d4a853]/40 rounded-xl p-5" data-id="element-146">
            <div className="flex items-center gap-3 mb-3" data-id="element-147">
              <div className="w-10 h-10 rounded-full bg-[#d4a853]/30 flex items-center justify-center" data-id="element-148">
                <EyeIcon className="w-5 h-5 text-[#d4a853]" data-id="element-149" />
              </div>
              <div data-id="element-150">
                <p className="font-dm-sans text-xs text-[#d4a853] uppercase tracking-wide" data-id="element-151">
                  Capítulo Especial
                </p>
                <h3 className="font-playfair text-lg text-[#f0c75e]" data-id="element-152">
                  Intuición y glándula pineal
                </h3>
              </div>
            </div>
            <p className="font-dm-sans text-sm text-[#f5f0e8]/80" data-id="element-153">
              Integración final, responsable y práctica
            </p>
          </motion.div>
        </div>

        {/* Bonuses Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.8
      }} className="mt-16" data-id="element-154">
          <div className="flex items-center justify-center gap-3 mb-8" data-id="element-155">
            <GiftIcon className="w-8 h-8 text-[#f0c75e]" data-id="element-156" />
            <h2 className="font-playfair text-2xl md:text-3xl text-[#f0c75e]" data-id="element-157">
              Los 3 BONUS
            </h2>
            <span className="font-dm-sans text-sm text-[#f5f0e8]/60" data-id="element-158">
              (para acelerar resultados)
            </span>
          </div>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-159">
            {bonuses.map((bonus, index) => <motion.div key={index} initial={{
            opacity: 0,
            scale: 0.95
          }} animate={isInView ? {
            opacity: 1,
            scale: 1
          } : {}} transition={{
            duration: 0.4,
            delay: 1 + index * 0.15
          }} className="bg-gradient-to-br from-[#0a0e27] to-[#1a1a4e] border-2 border-[#d4a853]/40 rounded-xl p-6" data-id="element-160">
                <div className="flex items-center gap-3 mb-4" data-id="element-161">
                  <bonus.icon className="w-6 h-6 text-[#d4a853]" data-id="element-162" />
                  <span className="font-dm-sans text-sm text-[#d4a853] font-semibold" data-id="element-163">
                    {bonus.title}
                  </span>
                </div>
                <h3 className="font-playfair text-xl text-[#f5f0e8] mb-3" data-id="element-164">
                  {bonus.name}
                </h3>
                <p className="font-dm-sans text-sm text-[#f5f0e8]/70" data-id="element-165">
                  {bonus.description}
                </p>
              </motion.div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}